import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'

import { PROMOTION_PLACEMENT_LOCKED_REASON, type PromotionPlacement } from '@onr/schema'
import { captureException } from '@sentry/react'
import { useParams } from '@tanstack/react-router'

import { useUpdatePromotionPlan } from '@/api/useUpdatePromotionPlan'
import { PROMOTION_SHOP_PLACEMENTS, type PromotionOption } from '@/components/model/PromotionOption'
import {
  type PromotionPlanChangeResult,
  PromotionShopView,
  type PromotionShopViewProps,
} from '@/components/templates/PromotionShopView'
import { usePromotionCampaign } from '@/state/configuration/usePromotionCampaign'
import { useRegionPromotionList } from '@/state/promotions/useRegionPromotionList'
import { useRegion } from '@/state/regions/useRegion'

import { useBackToPilote } from '../actions/useBackToPilote'

const isPlacementLocked = (error: unknown) => {
  const { code, details } = (error ?? {}) as { code?: string; details?: { reason?: string } }
  return code === 'functions/aborted' && details?.reason === PROMOTION_PLACEMENT_LOCKED_REASON
}

export const PromotionShopPage = () => {
  const intl = useIntl()
  const { initiativeId, regionId } = useParams({ from: '/promotions/$initiativeId/$regionId' })
  const { data: region } = useRegion({ regionId })
  const { data: promotions } = useRegionPromotionList({ regionId, initiativeId })
  const campaign = usePromotionCampaign(initiativeId)
  const { mutateAsync: updatePromotionPlan } = useUpdatePromotionPlan({ reportErrorToSentry: false })

  const options = useMemo<PromotionOption[]>(() => {
    const plan = region?.promotionPlacements[initiativeId] ?? []
    const locks = region?.promotionPlacementLocks?.[initiativeId] ?? {}
    return PROMOTION_SHOP_PLACEMENTS.map((placement) => {
      const pricing = campaign?.placements?.[placement]
      return {
        placement,
        state: plan.includes(placement) ? (locks[placement] ? 'locked' : 'inPlan') : 'available',
        fee: pricing?.fee,
        suggestedPrice: pricing?.suggestedPrice[intl.locale === 'en' ? 'en' : 'fr'],
      }
    })
  }, [campaign, initiativeId, intl.locale, region])

  const promotionsUsing = useCallback<PromotionShopViewProps['promotionsUsing']>(
    (placement) =>
      promotions
        .filter((promotion) => promotion.type === 'local' && promotion.placements.includes(placement))
        .map((promotion) => ({ id: promotion._id, partnerName: promotion.partnerName ?? promotion.title ?? '' })),
    [promotions]
  )

  const change = useCallback(
    async (placement: PromotionPlacement, inPlan: boolean): Promise<PromotionPlanChangeResult> => {
      try {
        await updatePromotionPlan({ regionId, initiativeId, placement, inPlan })
        return 'done'
      } catch (error) {
        if (isPlacementLocked(error)) {
          return 'locked'
        }
        captureException(error, { tags: { type: 'updatePromotionPlan' }, extra: { regionId, placement, inPlan } })
        return 'failed'
      }
    },
    [initiativeId, regionId, updatePromotionPlan]
  )

  const onBackToPiloteClicked = useBackToPilote()

  return (
    <PromotionShopView
      initiativeId={initiativeId}
      regionId={regionId}
      regionName={region?.name}
      year={campaign?.year}
      options={options}
      promotionsUsing={promotionsUsing}
      onAdd={async (placement) => change(placement, true)}
      onRemove={async (placement) => change(placement, false)}
      onBackToPiloteClicked={onBackToPiloteClicked}
    />
  )
}
